import trimEnd from "lodash/trimEnd";
import { refreshToken } from "@/@core/utils/refreshToken";

const createHeadersFromOptions = options => {
  let requestHeaders =
    (typeof options.headers === "function" ? options.headers() : options.headers) ||
    new Headers({
      Accept: "application/json",
    });
  if (
    !requestHeaders.has("Content-Type") &&
    !(options && (!options.method || options.method === "GET")) &&
    !(options && options.body && options.body instanceof FormData)
  ) {
    requestHeaders.set("Content-Type", "application/json");
  }

  return requestHeaders;
};

export default class FetchJson {
  constructor(url, options = {}) {
    this.url = trimEnd(url, "/");
    this.options = options;
    this.refreshToken = refreshToken;
  }
  get(path, options = {}) {
    return this.call(path, options);
  }

  post(path, data, options = {}) {
    return this.call(path, {
      ...options,
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  put(path, data, options = {}) {
    return this.call(path, {
      ...options,
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  patch(path, data, options = {}) {
    return this.call(path, {
      ...options,
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  delete(path, options = {}) {
    return this.call(path, {
      ...options,
      method: "DELETE",
    });
  }

  async call(path, options = {}) {
    const requestHeaders = createHeadersFromOptions({
      ...options,
      ...this.options,
    });

    /**
     * Some providers, such as MediaObject provider, has different URLS
     * for POST and GET requests. The line below adds an option to provide dynamic URL path
     * in different Http methods.
     */
    const requestUrl = this.url ? `${this.url}/${path}` : path;

    // This functions sets a custom type for PATCH methods
    // TODO: Should be set in createHeadersFromOptions function, however
    // I havent managed to modify headers in that function, please look into this later
    if (options && options.method === "PATCH") {
      requestHeaders.set("Content-Type", "application/merge-patch+json");
    }

    let response = await fetch(requestUrl, {
      ...options,
      ...this.options,
      credentials: "include",
      headers: requestHeaders,
    });

    /**
     * Response status 204 means that the action was successful, but no redirect is required
     * https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/204
     * In this case, we can safely resolve the promise
     * Not sure if 204 comes back only after deleting any entity, or in some other cases too.
     * Might need to amend the condition to somehow allow to define a custom success message.
     */
    if (response.status === 204) {
      return Promise.resolve({
        status: response.status,
        headers: response.headers,
        data: response.statusText,
      });
    }

    // If content body is not json, don't try to parse it
    if (response.headers.get("content-type") === "text/html; charset=UTF-8") {
      return Promise.resolve({
        status: response.status,
        headers: response.headers,
        data: response.statusText,
      });
    }

    let json = await response.json();
    let { status, statusText, headers } = await response;

    if (status === 401) {
      try {
        await this.refreshToken();
        response = await fetch(requestUrl, {
          ...options,
          ...this.options,
          headers: requestHeaders,
          credentials: "include",
        });
        json = await response.json();

        status = response.status;
        statusText = response.statusText;
        headers = await response.headers;
      } catch (e) {
        return Promise.reject({
          status,
          message: (json && json.message) || statusText,
          data: json,
        });
      }
    }

    if ((status < 200 || status >= 300) && status !== 401) {
      return Promise.reject({
        status,
        message: (json && json.message) || statusText,
        data: json,
        description: json?.["hydra:description"],
      });
    }

    return Promise.resolve({
      status,
      headers: [...headers.keys()].reduce((o, key) => {
        return {
          ...o,
          [key]: headers.get(key),
        };
      }, {}),
      data: json,
    });
  }
}
